import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { obterIdentificacao } from '../../api/configuracao-identificacao.js';
import { useConsulta } from '../../api/useConsulta.js';
import { Botao } from '../../componentes/Botao.js';
import { DialogoConfirmacao } from '../../componentes/DialogoConfirmacao.js';
import { IconeEtiqueta } from '../../componentes/Icones.js';
import { DesenhoDaEtiqueta } from '../etiquetas/DesenhoDaEtiqueta.js';
import { TAMANHO_PADRAO, tamanhoDeEtiqueta } from '../etiquetas/etiqueta.js';
import { FolhaDeEtiquetas } from '../etiquetas/FolhaDeImpressao.js';
import { useQrDosProdutos } from '../etiquetas/useQrDosProdutos.js';
import './QrDoProduto.css';

/**
 * O middleware em produção pode ainda não ter `GET /produtos/qrcode`: o 404
 * sem código vira `NAO_ENCONTRADO`, cuja tradução genérica ("o registro não
 * existe") faria a pessoa achar que o PRODUTO sumiu.
 */
const ROTA_AUSENTE = 'NAO_ENCONTRADO';

const MENSAGEM_DE_ROTA_AUSENTE =
  'O servidor ainda não gera o QR de produto. A etiqueta fica disponível quando o middleware for atualizado.';

type Produto = { readonly codPro: string; readonly controlaLote: boolean };

/** Link para Etiquetas › Por lote, só com o armazém em LOTE. */
function AtalhoDeLote({ codPro }: { readonly codPro: string }) {
  const consulta = useConsulta(obterIdentificacao);

  if (consulta.dados?.modoIdentificacaoQr !== 'LOTE') {
    return null;
  }

  const destino = `/etiquetas?${new URLSearchParams({ aba: 'lote', busca: codPro }).toString()}`;

  return (
    <p className="qr-do-produto__nota">
      O armazém identifica por lote, e este produto controla lote.{' '}
      <Link to={destino}>Etiquetas por lote deste produto</Link>
    </p>
  );
}

function PreviaDoQr({
  produto,
  aoFechar,
}: {
  readonly produto: Produto;
  readonly aoFechar: () => void;
}) {
  const codigos = useMemo(() => [produto.codPro], [produto.codPro]);
  const { estados, tentarDeNovo } = useQrDosProdutos(codigos);
  const estado = estados.get(produto.codPro);
  const tamanho = tamanhoDeEtiqueta(TAMANHO_PADRAO);
  const qr = estado?.situacao === 'pronto' ? estado.qr : null;

  return (
    <DialogoConfirmacao
      titulo={`QR Code de ${produto.codPro}`}
      rotuloConfirmar="Imprimir"
      rotuloCancelar="Fechar"
      variante="primario"
      confirmacaoBloqueada={qr === null}
      aoConfirmar={() => {
        window.print();
      }}
      aoCancelar={aoFechar}
    >
      <div className="qr-do-produto">
        {qr !== null && <DesenhoDaEtiqueta tamanho={tamanho} qr={qr} />}

        {estado?.situacao === 'falhou' && (
          <div className="qr-do-produto__falha" role="alert">
            <p>{estado.codigo === ROTA_AUSENTE ? MENSAGEM_DE_ROTA_AUSENTE : estado.mensagem}</p>
            {estado.codigo !== ROTA_AUSENTE && (
              <Botao
                altura="pequena"
                onClick={() => {
                  tentarDeNovo(produto.codPro);
                }}
              >
                Tentar de novo
              </Botao>
            )}
          </div>
        )}

        {(estado === undefined || estado.situacao === 'carregando') && (
          <p className="qr-do-produto__nota" role="status">
            Gerando o QR de {produto.codPro}…
          </p>
        )}

        <p className="qr-do-produto__nota">
          Etiqueta de {tamanho.rotulo}, uma por página. Tamanho e cópias se escolhem em Etiquetas.
        </p>

        {produto.controlaLote && <AtalhoDeLote codPro={produto.codPro} />}
      </div>

      {qr !== null && (
        <FolhaDeEtiquetas tamanho={tamanho}>
          <div className="folha-etiquetas__pagina">
            <DesenhoDaEtiqueta tamanho={tamanho} qr={qr} emMilimetros />
          </div>
        </FolhaDeEtiquetas>
      )}
    </DialogoConfirmacao>
  );
}

/**
 * Ação "QR Code" de uma linha de produto: abre a prévia da etiqueta com o QR
 * que o middleware gerou (`GET /produtos/qrcode`, matriz) e imprime só ela,
 * pela mesma folha de impressão da tela de Etiquetas. Quem renderiza já
 * conferiu `etiquetas.imprimir`.
 */
export function QrDoProduto({ produto }: { readonly produto: Produto }) {
  const [aberto, definirAberto] = useState(false);

  return (
    <>
      <Botao
        variante="discreto"
        altura="pequena"
        aria-label={`QR Code do produto ${produto.codPro}`}
        onClick={() => {
          definirAberto(true);
        }}
      >
        <IconeEtiqueta tamanho={14} />
        QR Code
      </Botao>
      {aberto && (
        <PreviaDoQr
          produto={produto}
          aoFechar={() => {
            definirAberto(false);
          }}
        />
      )}
    </>
  );
}
